import { createBrowserRouter, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { DialogLogin } from '../components/dialog-login';
import { KioskDispositivo } from '../components/kiosk-dispositivo';
import { TecladoTela } from '../components/teclado-tela';
import { TabletAtracao } from '../components/tablet-atracao';
import { ChamarGarcom } from '../components/chamar-garcom';
import { ConfigTablet } from '../components/config-tablet';
import { PwaKioskInstall } from '../components/pwa-kiosk-banner';
import { ContaSalao } from '../components/conta-salao';
import { CheckoutPage } from '../pages/checkout';
import { EntradaNumeroPage } from '../pages/entrada-numero';
import { CatalogoPage } from '../pages/catalogo';
import { ProdutoPage } from '../pages/produto';
import { CarrinhoPage } from '../pages/carrinho';
import { CheckoutFinalPage } from '../pages/checkout-final';
import { PedidoConfirmadoRota } from '../pages/pedido-confirmado/rota';
import { AvaliacaoPage } from '../pages/avaliacao';
import { ContaPage } from '../pages/conta';
import { MeusPedidosPage } from '../pages/meus-pedidos';
import { AdminPage } from '../pages/admin';
import { AdminLayout } from '../components/admin/layout';
import { GuardAcessoCardapio } from '../components/admin/guard-acesso-cardapio';
import { AdminCriarLojaPage } from '../pages/admin/criar-loja';
import { AdminConfiguracaoPage } from '../pages/admin/configuracao';
import { AdminIdentificadorPage } from '../pages/admin/identificador';
import { AdminCupomPage } from '../pages/admin/cupom';
import { AdminRedePage } from '../pages/admin/rede';
import { AdminHorarioPage } from '../pages/admin/horario';
import { AdminFormasPagamentoPage } from '../pages/admin/formas-pagamento';
import { AdminTaxaEntregaPage } from '../pages/admin/taxa-entrega';
import { AdminAvaliacoesPage } from '../pages/admin/avaliacoes';
import { AdminPedidosPage } from '../pages/admin/pedidos';
import { TotemPareamentoPage } from '../pages/totem/pareamento';
import { AdminCatalogoPage } from '../pages/admin/catalogo';

/** Raiz do app público: kiosk do dispositivo fixo (tablet) + teclado na tela + rotas. */
function RaizPublica(): JSX.Element {
  return (
    <>
      <KioskDispositivo />
      <TecladoTela />
      <TabletAtracao />
      <ChamarGarcom />
      {/* Configuração do aparelho: sem botão próprio, abre pelo evento (o "Sobre").
          Mora aqui, e não no catálogo kiosk, porque o catálogo não renderiza
          justamente nas situações em que o operador precisa configurar: loja
          carregando, erro de carga, e tablet sem identificador configurado.
          O totem de pedidos tem a sua, montada no app dele. */}
      <ConfigTablet semBotao />
      <ContaSalao />
      <PwaKioskInstall />
      <Outlet />
    </>
  );
}

/**
 * Layout que mantém o catálogo montado como "fundo" enquanto as telas-modal
 * (produto, carrinho, conta, meus pedidos, avaliação) são exibidas por cima via Outlet.
 * Como /catalogo e os modais compartilham este layout, a mesma instância de
 * CatalogoPage persiste na troca (scroll preservado, sem remontar nem
 * recontar tracking); o Outlet apenas alterna o modal sobreposto.
 */
function FundoCatalogo(): JSX.Element {
  return (
    <>
      <CatalogoPage />
      <Outlet />
    </>
  );
}

/**
 * Deep-link /login: abre o DialogLogin por cima do catálogo (mesmo modal usado
 * em todo o app — não há mais página de login dedicada). Para links externos /
 * bookmarks; dentro do app os botões abrem o DialogLogin direto. Ao fechar ou
 * concluir, volta pra tela anterior (ou catálogo, se entrou direto).
 */
function RotaLogin(): JSX.Element {
  const navigate = useNavigate();
  const voltar = (): void => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/catalogo', { replace: true });
  };
  return <DialogLogin aberto onFechar={voltar} onSucesso={voltar} />;
}

/** Rotas públicas (app do cliente). Tenant resolvido via header X-Tenant. */
export const router = createBrowserRouter([
  {
    element: <RaizPublica />,
    children: [
      { path: '/', element: <Navigate to="/catalogo" replace /> },
      { path: '/checkout/:uuid', element: <CheckoutPage /> },
      // Cupom da balança de autoatendimento: traduz a ETIQUETA ASSINADA da
      // mesa/comanda (`SCF1.C.5.aB3dEf9k2Q`) no código do ponto de acesso e
      // recarrega em /checkout/<codigo>. Fora do FundoCatalogo — não monta
      // catálogo, só resolve e sai.
      { path: '/entrada/:etiqueta', element: <EntradaNumeroPage /> },
      // Catálogo + telas que abrem como modal sobre ele (catálogo fica por baixo).
      {
        element: <FundoCatalogo />,
        children: [
          { path: '/catalogo' },
          { path: '/produto/:id', element: <ProdutoPage /> },
          { path: '/carrinho', element: <CarrinhoPage /> },
          { path: '/checkout', element: <CheckoutFinalPage /> },
          { path: '/pedido/:id', element: <PedidoConfirmadoRota /> },
          { path: '/avaliacao/:idPedido', element: <AvaliacaoPage /> },
          { path: '/conta', element: <ContaPage /> },
          { path: '/meus-pedidos', element: <MeusPedidosPage /> },
          { path: '/login', element: <RotaLogin /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

/** Backoffice (separado pra não exigir tenant). */
export const routerAdmin = createBrowserRouter([
  { path: '/admin', element: <AdminPage /> },
  { path: '/totem/parear', element: <TotemPareamentoPage /> },
  {
    path: '/admin/criar-loja',
    element: (
      <GuardAcessoCardapio>
        <AdminCriarLojaPage />
      </GuardAcessoCardapio>
    ),
  },
  {
    path: '/admin',
    element: (
      <GuardAcessoCardapio>
        <AdminLayout />
      </GuardAcessoCardapio>
    ),
    children: [
      { path: 'pedidos', element: <AdminPedidosPage /> },
      { path: 'configuracao', element: <AdminConfiguracaoPage /> },
      // Fora do menu lateral desde que cada aba de configuração passou a
      // gerenciar os seus pontos de acesso em modal. A rota fica de pé para
      // links e favoritos antigos, e como visão da loja inteira.
      { path: 'identificadores', element: <AdminIdentificadorPage /> },
      { path: 'cupons', element: <AdminCupomPage /> },
      { path: 'redes', element: <AdminRedePage /> },
      { path: 'horarios', element: <AdminHorarioPage /> },
      { path: 'formas-pagamento', element: <AdminFormasPagamentoPage /> },
      { path: 'taxa-entrega', element: <AdminTaxaEntregaPage /> },
      { path: 'catalogo', element: <AdminCatalogoPage /> },
      { path: 'avaliacoes', element: <AdminAvaliacoesPage /> },
      // 'municipios' migrou pra dentro da tela de Configuração (multi-select).
      // Mantém redirect pra evitar 404 em links antigos.
      { path: 'municipios', element: <Navigate to="/admin/configuracao" replace /> },
    ],
  },
  { path: '*', element: <Navigate to="/admin" replace /> },
]);
